import React, { useState, useEffect, useRef, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { createStyled } from '@mui/system';
import Popper from '@mui/material/Popper';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import InputBase from '@mui/material/InputBase';
import TextField from '@mui/material/TextField';
import { useTranslation } from 'react-i18next';
import { InputAdornment } from '@mui/material';

const StyledAutocompletePopper = styled('div')(({ listfontsize, theme }) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: '#77787B',
    fontSize: listfontsize
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: '#ffffff',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      borderRadius: 10,
      minHeight: 'auto',
      alignItems: 'flex-end',
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent'
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]: {
        backgroundColor: theme.palette.action.hover
      },
      p: {
        fontSize: '20px!important'
      },
      span: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }
    }
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative'
  }
}));

const PopperComponent = (props) => {
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
};

const StyledPopper = styled(Popper)(({ width, theme }) => ({
  border: '1px solid #D1D3D4',
  boxShadow: `0 8px 24px ${theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'}`,
  borderRadius: 10,
  width,
  zIndex: 1700,
  padding: '30px 15px',
  backgroundColor: '#ffffff'
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
  padding: 10,
  width: '100%',
  '& input': {
    borderRadius: 10,
    backgroundColor: '#ffffff',
    padding: '14px 14px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: '1px solid #D1D3D4',
    fontSize: 14
  }
}));

const textFieldStyles = (width) => ({
  width,
  backgroundColor: 'white',
  borderRadius: '10px',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #D1D3D4',
    borderRadius: '10px'
  },
  '& .MuiSvgIcon-root': {
    color: '#D1D3D4',
    fontSize: '24px'
  },
  color: '#283237',
  fontSize: '16px',
  fontWeight: '325',
  lineHeight: '19px',
  fieldset: {
    legend: {
      width: 'unset'
    }
  }
});

const SelectWithSearchInDropdown = ({
  showIndexes = false,
  forceWidth = '300px',
  list,
  placeholder,
  placeholderSearch,
  value,
  setValue,
  styles = {},
  listFontSize = 20
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [pendingValue, setPendingValue] = useState([]);

  const handleClick = () => {
    setPendingValue(value);
    setAnchorEl(ref.current);
    // setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setValue(pendingValue);
    if (anchorEl) {
      anchorEl.focus();
    }
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'github-label' : undefined;

  useEffect(() => {
    handleClose();
  }, [pendingValue]);

  const NestedPopperComponent = useCallback(
    (props) => <PopperComponent {...props} listfontsize={listFontSize} />,
    [listFontSize]
  );

  return (
    <div style={{ ...styles }}>
      <div id="empty" ref={ref} style={{ width: forceWidth, heigh: 0 }} />
      {!open ? (
        <TextField
          value={value?.label}
          placeholder={placeholder}
          onClick={handleClick}
          sx={textFieldStyles(forceWidth)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <ExpandMoreIcon sx={{ cursor: 'pointer' }} />
              </InputAdornment>
            )
          }}
        />
      ) : (
        <div style={{ height: '56px' }} />
      )}
      <StyledPopper id={id} open={open} anchorEl={anchorEl} placement="bottom-start" width={forceWidth}>
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Autocomplete
              PopperComponent={NestedPopperComponent}
              open
              onClose={(event, reason) => {
                if (reason === 'escape') {
                  handleClose();
                }
              }}
              value={pendingValue}
              onChange={(event, newValue, reason) => {
                if (event.type === 'keydown' && event.key === 'Backspace' && reason === 'removeOption') {
                  return;
                }
                setPendingValue(newValue);
              }}
              renderTags={() => null}
              noOptionsText={t('common.general.nothingfound')}
              renderOption={(props, option) => (
                <li
                  role="presentation"
                  {...props}
                  onClick={(e) => {
                    if (props.key === pendingValue.label) handleClose();
                    props.onClick(e);
                  }}
                >
                  {showIndexes && <p>#{props['data-option-index'] + 1}&nbsp;&nbsp;</p>}
                  <span>{option.label}</span>
                </li>
              )}
              options={list}
              getOptionLabel={(option) => option.label || ''}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={placeholderSearch}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </div>
  );
};

export default SelectWithSearchInDropdown;
